import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import { useAuth } from '../../contexts/AuthContext';
import RoleBasedHeader from '../../components/ui/RoleBasedHeader';
import AnnouncementBanner from '../../components/ui/AnnouncementBanner';
import Button from '../../components/ui/Button';
import Icon from '../../components/AppIcon';
import { paidAuditService } from '../../services/paidAuditService';
import { invoiceGenerator } from '../../utils/invoiceGenerator';
import PaymentMethodCard from '../billing-screen/components/PaymentMethodCard';
import SubscriptionPanel from '../billing-screen/components/SubscriptionPanel';
import InvoiceHistoryTable from '../billing-screen/components/InvoiceHistoryTable';
import RefundSupportForm from '../billing-screen/components/RefundSupportForm';
import toast from 'react-hot-toast';
import { getCurrentPricing, subscribeToPricing, getDiscountPercentage } from '../../constants/pricing';

// ── Brand gradient (matches AuditTypeSelector) ──────────────────────────────
const BRAND_GRAD = 'linear-gradient(135deg, #090A0B 0%, #001a4d 40%, #004AAD 75%, #0082FB 100%)';

// Helper function to calculate price with GST (only for INR)
const calculatePriceWithGST = (price, gstPercentage, currency) => {
  return price;
  // if (currency !== 'INR') return price;
  // if (!gstPercentage || gstPercentage <= 0) return price;
  // return price + (price * gstPercentage / 100);
};

// Helper function to format currency
const formatCurrency = (amount, currency) => {
  const sym = currency === 'INR' ? '₹' : '$';
  const locale = currency === 'INR' ? 'en-IN' : 'en-US';
  return `${sym}${(amount / 100).toLocaleString(locale, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};

// ── Reusable gradient button ─────────────────────────────────────────────────
const GradientButton = ({ children, onClick, loading = false, disabled = false, size = 'md', iconName, variant = 'solid', className = '' }) => {
  const sizeStyles = {
    sm: { padding: '8px 18px', fontSize: '13px', borderRadius: '100px' },
    md: { padding: '11px 24px', fontSize: '14px', borderRadius: '100px' },
    lg: { padding: '14px 32px', fontSize: '15px', borderRadius: '100px' },
  };
  const s = sizeStyles[size] || sizeStyles.md;

  const solidStyle = {
    background: BRAND_GRAD,
    color: '#fff',
    border: 'none',
    boxShadow: '0 4px 14px rgba(0,74,173,0.40)',
    fontWeight: 600,
    cursor: disabled || loading ? 'not-allowed' : 'pointer',
    opacity: disabled || loading ? 0.6 : 1,
    display: 'inline-flex',
    alignItems: 'center',
    gap: '7px',
    transition: 'filter 0.15s, transform 0.15s',
    fontFamily: 'inherit',
    ...s,
  };

  const outlineStyle = {
    background: '#fff',
    color: 'rgb(0,130,251)',
    border: '1.5px solid rgb(0,130,251)',
    boxShadow: 'none',
    fontWeight: 600,
    cursor: disabled || loading ? 'not-allowed' : 'pointer',
    opacity: disabled || loading ? 0.6 : 1,
    display: 'inline-flex',
    alignItems: 'center',
    gap: '7px',
    transition: 'background 0.15s',
    fontFamily: 'inherit',
    ...s,
  };

  const style = variant === 'solid' ? solidStyle : outlineStyle;

  return (
    <button
      style={style}
      onClick={onClick}
      disabled={disabled || loading}
      className={className}
      onMouseEnter={e => { if (!disabled && !loading && variant === 'solid') e.currentTarget.style.filter = 'brightness(1.08)'; }}
      onMouseLeave={e => { if (variant === 'solid') e.currentTarget.style.filter = ''; }}
    >
      {loading ? (
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" style={{ animation: 'spin 1s linear infinite' }}>
          <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="3" strokeDasharray="60" strokeDashoffset="20" strokeLinecap="round"/>
        </svg>
      ) : iconName ? (
        <Icon name={iconName} size={size === 'sm' ? 14 : 16} />
      ) : null}
      {children}
      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </button>
  );
};

const SettingsScreen = () => {
  const { userProfile, user } = useAuth();
  const [activeTab, setActiveTab] = useState('general');
  const [message, setMessage] = useState({ type: '', text: '' });
  const [isSaving, setIsSaving] = useState(false);
  const [paymentHistory, setPaymentHistory] = useState([]);
  const [isLoadingPayments, setIsLoadingPayments] = useState(true);
  const [billingActiveTab, setBillingActiveTab] = useState('overview');
  const [billingCurrency, setBillingCurrency] = useState('USD');
  
  // Dynamic pricing state
  const [pricing, setPricing] = useState(null);
  const [pricingLoading, setPricingLoading] = useState(true);

  const [generalSettings, setGeneralSettings] = useState({
    language: 'en',
    timezone: 'UTC',
    dashboardLayout: 'grid',
    defaultAnalysisParams: 'standard'
  });

  const [privacySettings, setPrivacySettings] = useState({
    dataSharing: false,
    analyticsOptOut: false,
    thirdPartyIntegrations: true
  });

  const [securitySettings, setSecuritySettings] = useState({
    sessionTimeout: '30',
    loginAlerts: true,
    twoFactorAuth: false
  });

  // Fetch pricing on mount and subscribe to updates
  useEffect(() => {
    const fetchPricing = async () => {
      try {
        const currentPricing = getCurrentPricing();
        setPricing(currentPricing);
        setPricingLoading(false);
      } catch (error) {
        console.error('Error fetching pricing:', error);
        setPricingLoading(false);
      }
    };

    fetchPricing();

    // Subscribe to real-time pricing updates
    const unsubscribe = subscribeToPricing((updatedPricing) => {
      console.log('SettingsScreen: Pricing updated', updatedPricing);
      setPricing(updatedPricing);
    });

    return () => unsubscribe();
  }, []);

  useEffect(() => {
    if (user?.id && activeTab === 'billing') {
      fetchPaymentHistory();
    }
  }, [user?.id, activeTab]);

  const fetchPaymentHistory = async () => {
    try {
      setIsLoadingPayments(true);
      const payments = await paidAuditService?.getUserPaymentOrders(user?.id);
      setPaymentHistory(payments || []);
    } catch (error) {
      console.error('Error fetching payment history:', error);
      toast?.error('Failed to load payment history');
    } finally {
      setIsLoadingPayments(false);
    }
  };

  const handleDownloadInvoice = (payment) => {
    try {
      invoiceGenerator?.downloadInvoice(payment, userProfile);
      toast?.success('Invoice downloaded successfully');
    } catch (error) {
      console.error('Error downloading invoice:', error);
      toast?.error('Failed to download invoice');
    }
  };

  const handleBulkExport = () => {
    try {
      const capturedPayments = paymentHistory?.filter(p => p?.payment_status === 'captured');
      if (capturedPayments?.length === 0) {
        toast?.error('No paid invoices to export');
        return;
      }
      capturedPayments?.forEach(payment => {
        invoiceGenerator?.downloadInvoice(payment, userProfile);
      });
      toast?.success(`Exported ${capturedPayments?.length} invoices`);
    } catch (error) {
      console.error('Error exporting invoices:', error);
      toast?.error('Failed to export invoices');
    }
  };

  const handleGeneralChange = (name, value) => {
    setGeneralSettings(prev => ({ ...prev, [name]: value }));
  };

  const handlePrivacyToggle = (name) => {
    setPrivacySettings(prev => ({ ...prev, [name]: !prev?.[name] }));
  };

  const handleSecurityToggle = (name) => {
    setSecuritySettings(prev => ({ ...prev, [name]: !prev?.[name] }));
  };

  const handleSaveSettings = async () => {
    setIsSaving(true);
    setMessage({ type: '', text: '' });
    setTimeout(() => {
      setMessage({ type: 'success', text: 'Settings saved successfully!' });
      setIsSaving(false);
      setTimeout(() => setMessage({ type: '', text: '' }), 3000);
    }, 1000);
  };

  const tabs = [
    { id: 'general', label: 'General', icon: 'Settings' },
    { id: 'notifications', label: 'Notifications', icon: 'Bell' },
    { id: 'privacy', label: 'Privacy', icon: 'Lock' },
    { id: 'security', label: 'Security', icon: 'Shield' },
    { id: 'billing', label: 'Billing & Payments', icon: 'CreditCard' }
  ];

  const capturedPayments = paymentHistory?.filter(p => p?.payment_status === 'captured');
  const pendingPayments = paymentHistory?.filter(p => p?.payment_status === 'pending');

  // Group total by currency
  const totalByCurrency = capturedPayments?.reduce((acc, p) => {
    const cur = p?.currency || 'USD';
    acc[cur] = (acc[cur] || 0) + (p?.amount || 0);
    return acc;
  }, {});

  const totalSpentDisplay = Object.entries(totalByCurrency || {})
    .map(([cur, amt]) => formatCurrency(amt, cur))
    .join(' + ') || '₹0.00';

  const billingTabs = [
    { id: 'overview', label: 'Overview', icon: 'LayoutDashboard' },
    { id: 'payment-methods', label: 'Payment Methods', icon: 'CreditCard' },
    { id: 'invoices', label: 'Invoices', icon: 'FileText' },
    { id: 'support', label: 'Support', icon: 'HelpCircle' }
  ];

  // Get dynamic pricing values
  const getDynamicPriceData = () => {
    if (!pricing || pricingLoading) {
      return {
        original: billingCurrency === 'INR' ? 27999 : 299,
        offer: billingCurrency === 'INR' ? 6999 : 75,
        gstPercentage: billingCurrency === 'INR' ? 18 : null,
        discountPercent: 75,
        badgeText: 'LAUNCH OFFER',
        bestFor: 'Delivered within 72 Hours',
        features: [
          'AI-powered analysis',
          'Human expert review',
          'Detailed feedback report',
          'Priority processing',
          'Actionable recommendations',
        ]
      };
    }

    const currencyData = pricing.paid[billingCurrency];
    const original = currencyData.original;
    const offer = currencyData.offer;
    const gstPercentage = billingCurrency === 'INR' ? (pricing.paid.INR?.gst || null) : null;
    const discountPercent = getDiscountPercentage(billingCurrency);
    
    return {
      original,
      offer,
      gstPercentage,
      discountPercent,
      badgeText: pricing.paid.badge || 'LAUNCH OFFER',
      bestFor: pricing.paid.best || 'Delivered within 72 Hours',
      features: pricing.paid.features || [
        'AI-powered analysis',
        'Human expert review',
        'Detailed feedback report',
        'Priority processing',
        'Actionable recommendations',
      ]
    };
  };

  const priceData = getDynamicPriceData();
  const finalOfferPrice = calculatePriceWithGST(priceData.offer, priceData.gstPercentage, billingCurrency);
  const finalOriginalPrice = calculatePriceWithGST(priceData.original, priceData.gstPercentage, billingCurrency);
  const savings = finalOriginalPrice - finalOfferPrice;
  
  const sym = billingCurrency === 'INR' ? '₹' : '$';
  const locale = billingCurrency === 'INR' ? 'en-IN' : 'en-US';

  // Paid features for display
  const paidFeats = priceData.features.map(f => ({ icon: 'Check', text: f }));

  // Toggle Switch Component
  const Toggle = ({ checked, onChange }) => (
    <button
      type="button"
      onClick={onChange}
      style={{
        position: 'relative',
        display: 'inline-flex',
        height: '24px',
        width: '44px',
        alignItems: 'center',
        borderRadius: '100px',
        border: 'none',
        cursor: 'pointer',
        background: checked ? BRAND_GRAD : '#cbd5e1',
        transition: 'background 0.2s',
        flexShrink: 0,
        boxShadow: checked ? '0 2px 8px rgba(0,74,173,0.35)' : 'none',
      }}
    >
      <span
        style={{
          display: 'inline-block',
          height: '18px',
          width: '18px',
          borderRadius: '50%',
          background: '#fff',
          boxShadow: '0 1px 4px rgba(0,0,0,0.18)',
          transform: checked ? 'translateX(22px)' : 'translateX(3px)',
          transition: 'transform 0.2s',
        }}
      />
    </button>
  );

  // Loading state for billing
  if (pricingLoading && activeTab === 'billing') {
    return (
      <>
        <Helmet>
          <title>Settings - myDeckAudit</title>
        </Helmet>
        <RoleBasedHeader />
        <AnnouncementBanner />
        <main className="pt-[64px] min-h-screen bg-background">
          <div className="container mx-auto px-4 py-8 max-w-6xl">
            <div className="flex items-center justify-center py-20">
              <div style={{ width: 40, height: 40, border: '3px solid #e2e8f0', borderTopColor: '#004AAD', borderRadius: '50%', animation: 'spin 0.8s linear infinite' }} />
              <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
            </div>
          </div>
        </main>
      </>
    );
  }

  return (
    <>
      <Helmet>
        <title>Settings - myDeckAudit</title>
        <meta name="description" content="Configure your myDeckAudit preferences and settings" />
      </Helmet>

      <RoleBasedHeader />
      <AnnouncementBanner />

      <main className="pt-[64px] md:pt-[64px] min-h-screen bg-background">
        <div className="container mx-auto px-4 md:px-6 lg:px-8 py-8 max-w-6xl">
          <div className="mb-8">
            <h1 className="text-3xl md:text-4xl font-bold text-foreground mb-2">Settings</h1>
            <p className="text-muted-foreground">Customize your myDeckAudit experience and manage preferences</p>
          </div>

          {message?.text && (
            <div className={`mb-6 p-4 rounded-lg flex items-start gap-3 ${
              message?.type === 'success'
                ? 'bg-success/10 border border-success/20'
                : 'bg-error/10 border border-error/20'
            }`}>
              <Icon
                name={message?.type === 'success' ? 'CheckCircle' : 'AlertCircle'}
                className={`w-5 h-5 flex-shrink-0 mt-0.5 ${message?.type === 'success' ? 'text-success' : 'text-error'}`}
              />
              <p className={`text-sm ${message?.type === 'success' ? 'text-success' : 'text-error'}`}>{message?.text}</p>
            </div>
          )}

          <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
            {/* Sidebar Navigation */}
            <div className="lg:col-span-1">
              <div className="bg-card border border-border rounded-xl p-2 space-y-1">
                {tabs?.map((tab) => (
                  <button
                    key={tab?.id}
                    onClick={() => setActiveTab(tab?.id)}
                    style={activeTab === tab?.id ? { background: BRAND_GRAD, color: '#fff' } : {}}
                    className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg text-left transition-colors ${
                      activeTab === tab?.id
                        ? 'font-semibold'
                        : 'text-muted-foreground hover:bg-muted'
                    }`}
                  >
                    <Icon name={tab?.icon} size={18} />
                    <span className="font-medium">{tab?.label}</span>
                  </button>
                ))}
              </div>
            </div>

            {/* Settings Content */}
            <div className="lg:col-span-3">
              <div className="bg-card border border-border rounded-xl p-6">

                {/* GENERAL */}
                {activeTab === 'general' && (
                  <div className="space-y-6">
                    <div>
                      <h2 className="text-2xl font-semibold text-foreground mb-1">General Settings</h2>
                      <p className="text-muted-foreground mb-6">Configure your basic preferences and default options</p>
                    </div>
                    <div className="space-y-4">
                      {[
                        { label: 'Language', key: 'language', opts: [['en','English'],['es','Spanish'],['fr','French'],['de','German']] },
                        { label: 'Timezone', key: 'timezone', opts: [['UTC','UTC (Coordinated Universal Time)'],['EST','EST (Eastern Standard Time)'],['PST','PST (Pacific Standard Time)'],['GMT','GMT (Greenwich Mean Time)']] },
                        { label: 'Dashboard Layout', key: 'dashboardLayout', opts: [['grid','Grid View'],['list','List View'],['compact','Compact View']] },
                        { label: 'Default Analysis Parameters', key: 'defaultAnalysisParams', opts: [['standard','Standard Analysis'],['detailed','Detailed Analysis'],['quick','Quick Analysis']] },
                      ].map(({ label, key, opts }) => (
                        <div key={key}>
                          <label className="block text-sm font-medium text-foreground mb-2">{label}</label>
                          <select
                            value={generalSettings?.[key]}
                            onChange={(e) => handleGeneralChange(key, e?.target?.value)}
                            className="w-full h-10 rounded-md border border-input bg-background px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                          >
                            {opts.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                          </select>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* NOTIFICATIONS */}
                {activeTab === 'notifications' && (
                  <div className="space-y-6">
                    <div>
                      <h2 className="text-2xl font-semibold text-foreground mb-1">Notification Settings</h2>
                      <p className="text-muted-foreground mb-6">Manage how and when you receive notifications</p>
                    </div>
                    <div className="space-y-4">
                      <div className="flex items-center justify-between py-4 border-b border-border">
                        <div>
                          <p className="font-medium text-foreground">Email Notifications</p>
                          <p className="text-sm text-muted-foreground">Receive email alerts for important updates</p>
                        </div>
                        <Toggle checked={true} onChange={() => {}} />
                      </div>
                      <div className="flex items-center justify-between py-4 border-b border-border">
                        <div>
                          <p className="font-medium text-foreground">In-App Notifications</p>
                          <p className="text-sm text-muted-foreground">Show notifications within the application</p>
                        </div>
                        <Toggle checked={true} onChange={() => {}} />
                      </div>
                      <div className="flex items-center justify-between py-4">
                        <div>
                          <p className="font-medium text-foreground">Communication Frequency</p>
                          <p className="text-sm text-muted-foreground">How often you receive digest emails</p>
                        </div>
                        <select className="h-10 rounded-md border border-input bg-background px-3 py-2 text-sm">
                          <option value="immediate">Immediate</option>
                          <option value="daily">Daily Digest</option>
                          <option value="weekly">Weekly Digest</option>
                        </select>
                      </div>
                    </div>
                  </div>
                )}

                {/* PRIVACY */}
                {activeTab === 'privacy' && (
                  <div className="space-y-6">
                    <div>
                      <h2 className="text-2xl font-semibold text-foreground mb-1">Privacy Settings</h2>
                      <p className="text-muted-foreground mb-6">Control your data sharing and privacy preferences</p>
                    </div>
                    <div className="space-y-4">
                      {[
                        { key: 'dataSharing', label: 'Data Sharing', desc: 'Share anonymized data to improve service quality' },
                        { key: 'analyticsOptOut', label: 'Analytics Opt-Out', desc: 'Disable usage analytics and tracking' },
                        { key: 'thirdPartyIntegrations', label: 'Third-Party Integrations', desc: 'Allow connections with external services' },
                      ].map(({ key, label, desc }, idx, arr) => (
                        <div key={key} className={`flex items-center justify-between py-4 ${idx < arr.length - 1 ? 'border-b border-border' : ''}`}>
                          <div>
                            <p className="font-medium text-foreground">{label}</p>
                            <p className="text-sm text-muted-foreground">{desc}</p>
                          </div>
                          <Toggle checked={privacySettings?.[key]} onChange={() => handlePrivacyToggle(key)} />
                        </div>
                      ))}
                      <div className="mt-6 p-4 bg-muted/50 rounded-lg">
                        <div className="flex items-start gap-3">
                          <Icon name="Info" className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
                          <div>
                            <p className="text-sm font-medium text-foreground mb-1">Data Export</p>
                            <p className="text-sm text-muted-foreground mb-3">Request a copy of all your data stored in myDeckAudit</p>
                            <GradientButton size="sm" iconName="Download" variant="outline">Request Data Export</GradientButton>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* SECURITY */}
                {activeTab === 'security' && (
                  <div className="space-y-6">
                    <div>
                      <h2 className="text-2xl font-semibold text-foreground mb-1">Security Settings</h2>
                      <p className="text-muted-foreground mb-6">Manage your account security and access controls</p>
                    </div>
                    <div className="space-y-4">
                      <div>
                        <label className="block text-sm font-medium text-foreground mb-2">Session Timeout</label>
                        <select
                          value={securitySettings?.sessionTimeout}
                          onChange={(e) => setSecuritySettings(prev => ({ ...prev, sessionTimeout: e?.target?.value }))}
                          className="w-full h-10 rounded-md border border-input bg-background px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                        >
                          <option value="15">15 minutes</option>
                          <option value="30">30 minutes</option>
                          <option value="60">1 hour</option>
                          <option value="120">2 hours</option>
                        </select>
                        <p className="text-xs text-muted-foreground mt-1">Automatically log out after period of inactivity</p>
                      </div>
                      {[
                        { key: 'loginAlerts', label: 'Login Alerts', desc: 'Receive notifications for new login attempts' },
                        { key: 'twoFactorAuth', label: 'Two-Factor Authentication', desc: 'Add an extra layer of security to your account' },
                      ].map(({ key, label, desc }) => (
                        <div key={key} className="flex items-center justify-between py-4 border-b border-border last:border-0">
                          <div>
                            <p className="font-medium text-foreground">{label}</p>
                            <p className="text-sm text-muted-foreground">{desc}</p>
                          </div>
                          <Toggle checked={securitySettings?.[key]} onChange={() => handleSecurityToggle(key)} />
                        </div>
                      ))}
                      <div className="mt-6 p-4 bg-warning/10 border border-warning/20 rounded-lg">
                        <div className="flex items-start gap-3">
                          <Icon name="AlertTriangle" className="w-5 h-5 text-warning flex-shrink-0 mt-0.5" />
                          <div>
                            <p className="text-sm font-medium text-foreground mb-1">Account Deactivation</p>
                            <p className="text-sm text-muted-foreground mb-3">Temporarily disable your account access</p>
                            <GradientButton size="sm" iconName="XCircle" variant="outline">Deactivate Account</GradientButton>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* BILLING with dynamic pricing */}
                {activeTab === 'billing' && (
                  <div className="space-y-6">
                    <div>
                      <h2 className="text-2xl font-semibold text-foreground mb-1">Billing & Payments</h2>
                      <p className="text-muted-foreground mb-6">Manage your payment methods, subscription, and invoices</p>
                    </div>

                    {/* Security Badge */}
                    <div
                      style={{ background: 'linear-gradient(135deg, rgba(9,10,11,0.04) 0%, rgba(0,26,77,0.06) 40%, rgba(0,74,173,0.08) 75%, rgba(0,130,251,0.07) 100%)' }}
                      className="border border-primary/20 rounded-xl p-4 mb-2 flex items-center gap-3"
                    >
                      <div
                        style={{ background: BRAND_GRAD }}
                        className="flex items-center justify-center w-10 h-10 rounded-full flex-shrink-0"
                      >
                        <Icon name="Shield" size={18} className="text-white" />
                      </div>
                      <div className="flex-1">
                        <p className="text-sm font-semibold text-foreground">Secure Payment Processing</p>
                        <p className="text-xs text-muted-foreground">PCI DSS compliant · 256-bit SSL encryption · Powered by Razorpay</p>
                      </div>
                    </div>

                    {/* Billing Sub-Tabs */}
                    <div className="border-b border-border mb-2">
                      <div className="flex gap-1 overflow-x-auto">
                        {billingTabs?.map((tab) => (
                          <button
                            key={tab?.id}
                            onClick={() => setBillingActiveTab(tab?.id)}
                            style={billingActiveTab === tab?.id ? { borderBottomColor: '#004AAD', color: '#004AAD' } : {}}
                            className={`flex items-center gap-2 px-4 py-3 text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${
                              billingActiveTab === tab?.id
                                ? 'border-primary'
                                : 'border-transparent text-muted-foreground hover:text-foreground hover:border-border'
                            }`}
                          >
                            <Icon name={tab?.icon} size={16} />
                            {tab?.label}
                          </button>
                        ))}
                      </div>
                    </div>

                    {/* OVERVIEW with dynamic pricing */}
                    {billingActiveTab === 'overview' && (
                      <div className="space-y-6">
                        {/* Stats Row */}
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                          <div style={{ background: BRAND_GRAD }} className="rounded-xl p-5 text-white relative overflow-hidden">
                            <div style={{ position: 'absolute', top: -20, right: -20, width: 80, height: 80, borderRadius: '50%', background: 'rgba(255,255,255,0.07)' }} />
                            <div className="flex items-center gap-2 mb-3">
                              <Icon name="DollarSign" size={16} className="text-white/80" />
                              <span className="text-xs text-white/80 font-semibold uppercase tracking-wider">Total Spent</span>
                            </div>
                            <p className="text-2xl font-bold tracking-tight">{totalSpentDisplay}</p>
                            <p className="text-xs text-white/60 mt-1">All-time</p>
                          </div>
                          <div className="bg-card border border-border rounded-xl p-5">
                            <div className="flex items-center gap-2 mb-3">
                              <div className="flex items-center justify-center w-7 h-7 rounded-lg bg-success/10">
                                <Icon name="CheckCircle2" size={14} className="text-success" />
                              </div>
                              <span className="text-xs text-muted-foreground font-semibold uppercase tracking-wider">Paid Invoices</span>
                            </div>
                            <p className="text-2xl font-bold text-foreground">{capturedPayments?.length || 0}</p>
                            <p className="text-xs text-muted-foreground mt-1">Successful payments</p>
                          </div>
                          <div className="bg-card border border-border rounded-xl p-5">
                            <div className="flex items-center gap-2 mb-3">
                              <div className="flex items-center justify-center w-7 h-7 rounded-lg bg-warning/10">
                                <Icon name="Clock" size={14} className="text-warning" />
                              </div>
                              <span className="text-xs text-muted-foreground font-semibold uppercase tracking-wider">Pending</span>
                            </div>
                            <p className="text-2xl font-bold text-foreground">{pendingPayments?.length || 0}</p>
                            <p className="text-xs text-muted-foreground mt-1">Awaiting payment</p>
                          </div>
                        </div>

                        {/* Paid Deck Audit Plan Card with Dynamic Pricing */}
                        <div
                          style={{
                            background: 'linear-gradient(160deg, rgba(0,130,251,0.06) 0%, rgba(0,74,173,0.09) 100%)',
                            border: '1.5px solid rgb(0,130,251)',
                            borderRadius: 20,
                            padding: '28px 24px 24px',
                            position: 'relative',
                            overflow: 'visible',
                          }}
                        >
                          {/* Launch badge */}
                          <div style={{
                            position: 'absolute', top: -14, left: '50%', transform: 'translateX(-50%)',
                            background: 'linear-gradient(135deg, #f59e0b, #d97706)',
                            color: '#fff', fontSize: 10, fontWeight: 700, letterSpacing: '0.06em',
                            padding: '5px 14px', borderRadius: 100, whiteSpace: 'nowrap',
                            boxShadow: '0 2px 8px rgba(245,158,11,0.4)',
                            display: 'flex', alignItems: 'center', gap: 5,
                          }}>
                            🚀 {priceData.badgeText} — {priceData.discountPercent}% OFF
                          </div>

                          {/* Header row: label + currency toggle */}
                          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 10, marginBottom: 16, marginTop: 6 }}>
                            <p style={{ margin: 0, fontSize: 11, fontWeight: 700, letterSpacing: '0.09em', color: 'rgb(0,130,251)', textTransform: 'uppercase' }}>
                              PAID DECK AUDIT
                            </p>
                            <div style={{
                              display: 'inline-flex', alignItems: 'center',
                              background: '#f1f5f9', borderRadius: 100,
                              padding: 3, border: '1px solid #e2e8f0', flexShrink: 0,
                            }}>
                              {['INR', 'USD'].map(c => (
                                <button
                                  key={c}
                                  onClick={() => setBillingCurrency(c)}
                                  style={{
                                    padding: '4px 14px', borderRadius: 100, border: 'none',
                                    cursor: 'pointer', fontSize: 12, fontWeight: 600,
                                    fontFamily: 'inherit', whiteSpace: 'nowrap',
                                    transition: 'all 0.15s',
                                    ...(billingCurrency === c
                                      ? { background: BRAND_GRAD, color: '#fff', boxShadow: '0 1px 6px rgba(0,74,173,0.35)' }
                                      : { background: 'transparent', color: '#64748b' }),
                                  }}
                                >
                                  {c}
                                </button>
                              ))}
                            </div>
                          </div>

                          {/* Pricing with GST */}
                          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 4 }}>
                            <span style={{ fontSize: 14, fontWeight: 500, color: '#94a3b8', textDecoration: 'line-through' }}>
                              {sym}{finalOriginalPrice.toLocaleString(locale)}
                            </span>
                            <span style={{
                              background: '#fef3c7', border: '1px solid #fde68a',
                              color: '#92400e', fontSize: 10, fontWeight: 700,
                              padding: '2px 8px', borderRadius: 6,
                            }}>{priceData.discountPercent}% OFF</span>
                          </div>
                          <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, marginBottom: 2 }}>
                            <span style={{ fontSize: 44, fontWeight: 700, color: '#0f172a', lineHeight: 1, letterSpacing: '-0.02em' }}>
                              {sym}{finalOfferPrice.toLocaleString(locale)}
                            </span>
                            {billingCurrency === 'INR' && priceData.gstPercentage && priceData.gstPercentage > 0 && (
                              <span style={{
                                fontSize: 10, fontWeight: 500, color: '#10b981',
                                background: 'rgba(16,185,129,0.1)', padding: '2px 8px', borderRadius: 100,
                              }}>
                                + {priceData.gstPercentage}% GST
                              </span>
                            )}
                          </div>
                          <p style={{ fontSize: 12, color: '#64748b', margin: '0 0 10px' }}>One-time · Limited time</p>

                          {/* Savings callout */}
                          <div style={{
                            display: 'inline-flex', alignItems: 'center', gap: 6,
                            background: 'linear-gradient(135deg, rgba(9,10,11,0.04), rgba(0,130,251,0.08))',
                            border: '1px solid rgba(0,130,251,0.25)', borderRadius: 8,
                            padding: '5px 10px', marginBottom: 14,
                          }}>
                            <svg width="12" height="12" viewBox="0 0 24 24" fill="none">
                              <path d="M12 2H7a2 2 0 0 0-2 2v5l9 9 7-7-9-9z" stroke="#004AAD" strokeWidth="2" strokeLinejoin="round"/>
                              <circle cx="8.5" cy="8.5" r="1" fill="#004AAD"/>
                            </svg>
                            <span style={{
                              fontSize: 11, fontWeight: 700,
                              background: 'linear-gradient(135deg, #004AAD, #0082FB)',
                              WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent',
                            }}>
                              You save {sym}{savings.toLocaleString(locale)} on this order
                            </span>
                          </div>

                          {/* AI + Human badge */}
                          <div style={{
                            display: 'inline-flex', alignItems: 'center', gap: 6,
                            background: 'rgba(245,158,11,0.12)', border: '1px solid rgba(245,158,11,0.3)',
                            borderRadius: 100, padding: '5px 12px', marginBottom: 18,
                          }}>
                            <svg width="13" height="13" viewBox="0 0 24 24">
                              <polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2" fill="#f59e0b"/>
                            </svg>
                            <span style={{ fontSize: 12, fontWeight: 600, color: '#b45309' }}>AI + Human Powered</span>
                          </div>

                          {/* Features grid */}
                          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px 16px', marginBottom: 22 }}>
                            {paidFeats.map((f, i) => (
                              <div key={i} style={{ display: 'flex', alignItems: 'flex-start', gap: 8 }}>
                                <svg width="16" height="16" viewBox="0 0 20 20" fill="none" style={{ flexShrink: 0, marginTop: 2 }}>
                                  <path d="M4 10.5L8 14.5L16 6.5" stroke="#16a34a" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"/>
                                </svg>
                                <span style={{ fontSize: 13, color: '#1e293b', lineHeight: 1.45 }}>{f.text}</span>
                              </div>
                            ))}
                          </div>

                          {/* CTA */}
                          <button
                            style={{
                              width: '100%', padding: '14px 0', borderRadius: 100,
                              background: BRAND_GRAD, color: '#fff', border: 'none',
                              fontSize: 14, fontWeight: 600, cursor: 'pointer',
                              boxShadow: '0 4px 14px rgba(0,74,173,0.40)',
                              display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
                              fontFamily: 'inherit',
                            }}
                            onClick={() => setBillingActiveTab('payment-methods')}
                          >
                            <Icon name="CreditCard" size={16} className="text-white" />
                            Proceed to Payment
                          </button>
                          <p style={{ margin: '6px 0 0', fontSize: 11, color: '#64748b', textAlign: 'center', fontStyle: 'italic' }}>
                            {priceData.bestFor}
                          </p>
                        </div>

                        {/* Subscription Panel */}
                        <SubscriptionPanel userProfile={userProfile} paymentHistory={paymentHistory} />

                        {/* Recent Invoices */}
                        <div className="bg-card border border-border rounded-xl p-6">
                          <div className="flex items-center justify-between mb-5">
                            <h2 className="text-lg font-semibold text-foreground flex items-center gap-2">
                              <Icon name="FileText" size={20} />
                              Recent Invoices
                            </h2>
                            <GradientButton
                              size="sm"
                              iconName="Eye"
                              variant="outline"
                              onClick={() => setBillingActiveTab('invoices')}
                            >
                              View All
                            </GradientButton>
                          </div>

                          {isLoadingPayments ? (
                            <div className="flex items-center justify-center py-10">
                              <div style={{ width: 28, height: 28, border: '3px solid #e2e8f0', borderTopColor: '#004AAD', borderRadius: '50%', animation: 'spin 0.8s linear infinite' }} />
                              <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
                            </div>
                          ) : paymentHistory?.length > 0 ? (
                            <div className="space-y-3">
                              {paymentHistory?.slice(0, 3)?.map((payment) => (
                                <div
                                  key={payment?.id}
                                  className="flex items-center justify-between p-4 bg-muted/30 rounded-lg border border-border hover:border-primary/30 transition-colors"
                                >
                                  <div className="flex items-center gap-3">
                                    <div
                                      style={{ background: 'linear-gradient(135deg, rgba(0,74,173,0.1), rgba(0,130,251,0.1))' }}
                                      className="flex items-center justify-center w-9 h-9 rounded-lg"
                                    >
                                      <Icon name="FileText" size={16} style={{ color: '#004AAD' }} />
                                    </div>
                                    <div>
                                      <p className="font-medium text-foreground text-sm">Paid Deck Audit</p>
                                      <p className="text-xs text-muted-foreground">
                                        {new Date(payment?.paid_at || payment?.created_at)?.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                                      </p>
                                    </div>
                                  </div>
                                  <div className="flex items-center gap-3">
                                    <div className="text-right">
                                      <span className="font-bold text-foreground">
                                        {formatCurrency(payment?.amount, payment?.currency)}
                                      </span>
                                      {payment?.payment_status === 'captured' && (
                                        <span
                                          className="block text-xs font-medium mt-0.5"
                                          style={{ color: '#16a34a' }}
                                        >
                                          Paid
                                        </span>
                                      )}
                                    </div>
                                    {payment?.payment_status === 'captured' && (
                                      <button
                                        onClick={() => handleDownloadInvoice(payment)}
                                        style={{
                                          width: 32, height: 32, borderRadius: '50%',
                                          background: BRAND_GRAD,
                                          border: 'none', cursor: 'pointer',
                                          display: 'flex', alignItems: 'center', justifyContent: 'center',
                                          boxShadow: '0 2px 8px rgba(0,74,173,0.30)',
                                          flexShrink: 0,
                                        }}
                                        title="Download invoice"
                                      >
                                        <Icon name="Download" size={14} className="text-white" />
                                      </button>
                                    )}
                                  </div>
                                </div>
                              ))}
                            </div>
                          ) : (
                            <div className="text-center py-12">
                              <div
                                style={{ background: 'linear-gradient(135deg, rgba(0,74,173,0.07), rgba(0,130,251,0.07))', width: 64, height: 64, borderRadius: '50%' }}
                                className="flex items-center justify-center mx-auto mb-4"
                              >
                                <Icon name="FileText" size={28} style={{ color: '#004AAD' }} />
                              </div>
                              <p className="font-medium text-foreground mb-1">No invoices yet</p>
                              <p className="text-sm text-muted-foreground">Your invoices will appear here after your first purchase</p>
                            </div>
                          )}
                        </div>
                      </div>
                    )}

                    {billingActiveTab === 'payment-methods' && (
                      <div className="space-y-6"><PaymentMethodCard /></div>
                    )}

                    {billingActiveTab === 'invoices' && (
                      <div className="space-y-6">
                        <InvoiceHistoryTable
                          paymentHistory={paymentHistory}
                          isLoading={isLoadingPayments}
                          onDownloadInvoice={handleDownloadInvoice}
                          onBulkExport={handleBulkExport}
                          userProfile={userProfile}
                        />
                      </div>
                    )}

                    {billingActiveTab === 'support' && (
                      <div className="space-y-6"><RefundSupportForm paymentHistory={paymentHistory} /></div>
                    )}
                  </div>
                )}

                {/* Save Button for non-billing tabs */}
                {activeTab !== 'billing' && (
                  <div className="mt-8 pt-6 border-t border-border">
                    <GradientButton
                      onClick={handleSaveSettings}
                      loading={isSaving}
                      iconName="Save"
                    >
                      Save Settings
                    </GradientButton>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </main>
    </>
  );
};

export default SettingsScreen;